/**
 * PDP Buy-Box GSAP Animation
 * Handles sticky positioning and content toggle animations for the product detail page buy-box
 * Desktop only (>=1024px)
 */

(function() {
  'use strict';

  // State variables
  let scrollTriggerInstance = null;
  let lastScrollY = window.scrollY;
  let isContentVisible = true;
  let scrollListenerAttached = false;
  let isSnapScrolling = false; // Flag to pause scroll handling during snap animation

  /**
   * Reusable toggle function for content1/trigger1
   * Can be called by scroll animations, trigger1 click, or manual triggers
   * @param {boolean} showContent - true to show content1, false to show trigger1
   */
  function toggleBuyBoxContent(showContent) {
    const content = document.querySelector('.animation-info-content');
    const trigger = document.querySelector('.animation-info-open-trigger');

    if (!content || !trigger) return;

    // Kill any in-progress tweens to prevent stale callbacks from firing
    gsap.killTweensOf(content);
    gsap.killTweensOf(trigger);

    if (showContent) {
      // Set CSS classes immediately — not in callbacks — so rapid calls can't leave both hidden
      content.classList.remove('animation-hidden');
      trigger.classList.remove('animation-visible');

      // Show content1, hide trigger1
      // Slide up from below with fade: 0.3s
      gsap.fromTo(content,
          { y: 20, opacity: 0, height: 0 },
          {
            y: 0,
            opacity: 1,
            height: 'auto',
            duration: 0.3,
            ease: 'power2.out'
          }
      );

      // Fade out trigger and slide down
      gsap.to(trigger, {
        y: 20,
        opacity: 0,
        height: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    } else {
      // Set CSS classes immediately
      trigger.classList.add('animation-visible');
      content.classList.add('animation-hidden');

      // Slide down and fade out content: 0.3s
      gsap.to(content, {
        y: 20,
        opacity: 0,
        height: 0,
        duration: 0.3,
        ease: 'power2.out'
      });

      // Slide up from below with fade for trigger
      gsap.fromTo(trigger,
          { y: 20, opacity: 0, height: 0 },
          {
            y: 0,
            opacity: 1,
            height: 'auto',
            duration: 0.3,
            ease: 'power2.out'
          }
      );
    }

    // Update state
    isContentVisible = showContent;
  }

  /**
   * Initialize scroll listener for content toggle
   */
  function initScrollListener() {
    if (scrollListenerAttached) return;

    const runArea = document.querySelector('.animation-buy-box-run-area');
    if (!runArea) return;

    function handleScroll() {
      // Skip scroll handling during snap animation
      if (isSnapScrolling) return;

      const currentScrollY = window.scrollY;
      const runAreaRect = runArea.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Check if user is scrolling UP from below the run-area
      // and the run-area bottom is now visible in viewport
      if (currentScrollY < lastScrollY) {
        // How far above the viewport bottom the run-area bottom should land after the snap.
        // 24px matches the buy-box parked offset; the -150 lets the snap overshoot past the
        // run-area bottom (final position is 126px below the viewport bottom).
        const SNAP_OFFSET_PX = 24 - 150;

        // Run-area bottom is visible but user was below it
        // (bottom is between 0 and viewport height, but above the snap-offset threshold)
        const isRunAreaBottomVisible = runAreaRect.bottom > 0 && runAreaRect.bottom < viewportHeight;

        if (isRunAreaBottomVisible && runAreaRect.bottom < viewportHeight - SNAP_OFFSET_PX) {
          // Auto-scroll to snap run-area bottom to viewport bottom (with SNAP_OFFSET_PX offset)
          const targetScrollY = window.scrollY + runAreaRect.bottom - viewportHeight + SNAP_OFFSET_PX;

          // Pause scroll handling during animation
          isSnapScrolling = true;

          // Animate scroll over 300ms with easeOutQuad
          const startScrollY = window.scrollY;
          const distance = targetScrollY - startScrollY;
          const duration = 300;
          let startTime = null;

          function animateScroll(currentTime) {
            if (!startTime) startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // easeOutQuad easing
            const easeProgress = 1 - (1 - progress) * (1 - progress);
            window.scrollTo(0, startScrollY + distance * easeProgress);

            if (progress < 1) {
              requestAnimationFrame(animateScroll);
            } else {
              // Animation complete - resume scroll handling and open buy-box
              isSnapScrolling = false;
              lastScrollY = window.scrollY;
              if (!isContentVisible) {
                toggleBuyBoxContent(true);
              }
            }
          }
          requestAnimationFrame(animateScroll);

          return;
        }
      }

      // Existing toggle logic (only when fully within run-area)
      const isWithinRunArea = runAreaRect.top < viewportHeight && runAreaRect.bottom > 0;

      if (isWithinRunArea) {
        // Scrolling down: hide content1, show trigger1 (immediately)
        if (currentScrollY > lastScrollY && currentScrollY > 0 && isContentVisible) {
          toggleBuyBoxContent(false);
        }
        // Scrolling up: show content1, hide trigger1 (immediately)
        else if (currentScrollY < lastScrollY && !isContentVisible) {
          toggleBuyBoxContent(true);
        }
      }

      lastScrollY = currentScrollY;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollListenerAttached = true;
  }

  /**
   * Initialize trigger1 click handler
   */
  function initTriggerClickHandler() {
    const trigger = document.querySelector('.animation-info-open-trigger');
    if (!trigger) return;

    trigger.addEventListener('click', () => {
      toggleBuyBoxContent(true);
      isContentVisible = true;
    });
  }

  /**
   * Initialize desktop animation
   */
  function initDesktopAnimation() {
    if (window.innerWidth < 1024) return;

    const buyBox = document.querySelector('.animation-buy-box');
    const runArea = document.querySelector('.animation-buy-box-run-area');

    if (!buyBox || !runArea) return;

    // Set initial buy-box positioning (no animation to prevent layout shift)
    gsap.set(buyBox, {
      position: 'fixed',
      bottom: '24px',
      left: '50%',
      xPercent: -50,
      width: '390px',
      zIndex: 300
    });

    // Animate buy-box in after GSAP initializes
    gsap.fromTo(buyBox,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', delay: 0.5 }
    );

    // Create ScrollTrigger to pin buy-box at bottom when reaching end of pdp-area
    // Buy-box stays visible and stops 24px above pdp-area bottom
    // Recalculates on every scroll to handle dynamic image loading
    scrollTriggerInstance = ScrollTrigger.create({
      trigger: runArea,
      start: 'top top',
      end: () => `+=${runArea.offsetHeight}`, // Dynamically calculate end based on current height
      invalidateOnRefresh: true, // Recalculate when images load
      onUpdate: () => {
        // Recalculate everything on each scroll frame to handle dynamic content
        const runAreaRect = runArea.getBoundingClientRect();
        const runAreaBottom = runAreaRect.bottom;
        const viewportHeight = window.innerHeight;

        // Calculate if pdp-area bottom is at or above viewport bottom
        if (runAreaBottom <= viewportHeight - 24) {
          // pdp-area is ending - switch to absolute positioning with bottom: 24px
          // This keeps buy-box 24px from the bottom of pdp-area
          gsap.set(buyBox, {
            position: 'absolute',
            bottom: '24px',
            left: '50%',
            xPercent: -50
          });
        } else {
          // Normal fixed position at bottom of viewport
          gsap.set(buyBox, {
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            xPercent: -50
          });
        }
      },
      markers: false // Set to true for debugging
    });

    // Refresh ScrollTrigger when images load to recalculate positions
    const images = runArea.querySelectorAll('img, video');
    images.forEach(img => {
      img.addEventListener('load', () => {
        ScrollTrigger.refresh();
      });
    });

    // Initialize scroll listener for content toggle
    initScrollListener();

    // Initialize trigger1 click handler
    initTriggerClickHandler();
  }

  /**
   * Destroy desktop animation and reset for mobile
   */
  function destroyDesktopAnimation() {
    const buyBox = document.querySelector('.animation-buy-box');

    if (scrollTriggerInstance) {
      scrollTriggerInstance.kill();
      scrollTriggerInstance = null;
    }

    // Reset buy-box positioning for mobile
    if (buyBox) {
      gsap.set(buyBox, { clearProps: 'all' });
    }

    // Reset scroll state
    lastScrollY = window.scrollY;
    isContentVisible = true;

    // Note: We don't remove the scroll listener as it won't affect mobile
  }

  /**
   * Handle window resize
   */
  function handleResize() {
    let resizeTimeout;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth >= 1024) {
          if (!scrollTriggerInstance) {
            initDesktopAnimation();
          }
        } else {
          if (scrollTriggerInstance) {
            destroyDesktopAnimation();
          }
        }
      }, 250);
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Check if GSAP and ScrollTrigger are loaded
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.error('GSAP or ScrollTrigger not loaded');
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Initialize desktop animation
    initDesktopAnimation();

    // Handle window resize
    handleResize();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Make toggleBuyBoxContent globally accessible for manual triggers
  window.toggleBuyBoxContent = toggleBuyBoxContent;

})();
